import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabase';
import { PublicArtist } from '../types/publicArtist';

const BATCH_SIZE = 48;
/** Prefetch the next batch when the in-memory queue drops below this. */
const PREFETCH_THRESHOLD = 16;

export function usePublicArtists() {
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const queueRef = useRef<PublicArtist[]>([]);
  const offsetRef = useRef(0);
  const totalRef = useRef<number | null>(null);
  const fetchingRef = useRef(false);

  const fetchBatch = useCallback(async (offset: number): Promise<PublicArtist[]> => {
    const { data, error: rpcError } = await supabase.rpc('get_public_artists', {
      limit_count: BATCH_SIZE,
      offset_count: offset,
    });
    if (rpcError) throw rpcError;
    return (data as PublicArtist[] | null) ?? [];
  }, []);

  const loadNextBatch = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      let offset = offsetRef.current;
      let batch = await fetchBatch(offset);

      // Empty batch past the end — wrap to the top.
      if (batch.length === 0 && offset !== 0) {
        offsetRef.current = 0;
        batch = await fetchBatch(0);
      }

      if (batch.length > 0) {
        queueRef.current.push(...batch);
        offsetRef.current = offset + batch.length;
        const total = totalRef.current;
        if (total !== null && offsetRef.current >= total) {
          offsetRef.current = 0;
        }
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [fetchBatch]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: countData, error: countError } = await supabase.rpc(
          'get_public_artists_count'
        );
        if (countError) throw countError;
        if (cancelled) return;

        const total = Number(countData ?? 0);
        totalRef.current = total;
        setTotalCount(total);

        if (total > 0) {
          offsetRef.current = 0;
          queueRef.current = [];
          await loadNextBatch();
        }
        if (!cancelled) setReady(true);
      } catch (err) {
        console.error('Failed to load public artists:', err);
        if (!cancelled) {
          setError('Could not load community artists.');
          setReady(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, [loadNextBatch]);

  const takeNextArtist = useCallback((): PublicArtist | null => {
    const queue = queueRef.current;
    if (queue.length === 0) return null;

    const artist = queue.shift() ?? null;

    if (queue.length < PREFETCH_THRESHOLD) {
      void loadNextBatch();
    }

    return artist;
  }, [loadNextBatch]);

  const peekQueueSize = useCallback(() => queueRef.current.length, []);

  return {
    totalCount,
    loading,
    error,
    ready,
    hasArtists: (totalCount ?? 0) > 0,
    takeNextArtist,
    peekQueueSize,
  };
}
